import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Building2,
  Users,
  FileText,
  Wrench,
  TrendingUp,
  Shield,
  Clock,
  ChartBar,
  CheckCircle2,
  ArrowRight,
} from "lucide-react";

export default function LandlordPage() {
  const features = [
    {
      icon: Building2,
      title: "Property Management",
      description:
        "Manage multiple properties from a single dashboard. Track occupancy, rent collection, and property performance.",
    },
    {
      icon: Users,
      title: "Tenant Screening",
      description:
        "Access tenant applications, verify documents, and conduct background checks all in one place.",
    },
    {
      icon: FileText,
      title: "Digital Lease Agreements",
      description:
        "Create, customize, and sign lease agreements digitally. Store all documents securely in the cloud.",
    },
    {
      icon: Wrench,
      title: "Maintenance Tracking",
      description:
        "Receive and manage maintenance requests. Assign contractors and track repair progress in real-time.",
    },
    {
      icon: TrendingUp,
      title: "Financial Analytics",
      description:
        "View detailed reports on rental income, expenses, and ROI. Make data-driven investment decisions.",
    },
    {
      icon: Shield,
      title: "Secure Payments",
      description:
        "Collect rent payments securely. Automated reminders and late payment tracking included.",
    },
  ];

  const benefits = [
    "Reduce vacancy periods with powerful listing tools",
    "Save time with automated rent collection and reminders",
    "Improve tenant satisfaction with quick maintenance response",
    "Access your properties anywhere with mobile-friendly interface",
    "Stay compliant with digital document management",
    "Scale your portfolio with enterprise-grade tools",
  ];

  const stats = [
    { label: "Active Landlords", value: "2,500+", icon: Users },
    { label: "Properties Managed", value: "8,000+", icon: Building2 },
    { label: "Average Response Time", value: "< 2hrs", icon: Clock },
    { label: "Satisfaction Rate", value: "98%", icon: ChartBar },
  ];

  return (
    <div className="container mx-auto px-4 py-12 space-y-20">
      {/* Hero Section */}
      <section className="text-center space-y-6 py-12">
        <Badge className="mb-4" variant="secondary">
          FOR PROPERTY OWNERS
        </Badge>
        <h1 className="text-4xl md:text-6xl font-bold tracking-tight">
          Manage Your Properties
          <span className="block text-primary mt-2">With Confidence</span>
        </h1>
        <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
          Professional property management tools designed for Nigerian
          landlords. Streamline operations, maximize returns, and grow your
          portfolio.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center pt-6">
          <Button size="lg" asChild>
            <Link to="/landlord/dashboard">
              Access Dashboard
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </Button>
          <Button size="lg" variant="outline" asChild>
            <Link to="/auth/signup">Start Free Trial</Link>
          </Button>
        </div>
      </section>

      {/* Stats Section */}
      <section className="grid grid-cols-2 md:grid-cols-4 gap-6">
        {stats.map((stat) => (
          <Card key={stat.label} className="text-center">
            <CardContent className="pt-6 space-y-2">
              <stat.icon className="h-8 w-8 mx-auto text-primary" />
              <div className="text-3xl font-bold">{stat.value}</div>
              <div className="text-sm text-muted-foreground">{stat.label}</div>
            </CardContent>
          </Card>
        ))}
      </section>

      {/* Features Section */}
      <section className="space-y-12">
        <div className="text-center space-y-4">
          <h2 className="text-3xl md:text-4xl font-bold">
            Everything You Need to Manage Properties
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Powerful features that save you time and help you make better
            decisions
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((feature) => (
            <Card
              key={feature.title}
              className="hover:shadow-lg transition-shadow"
            >
              <CardHeader>
                <feature.icon className="h-10 w-10 text-primary mb-2" />
                <CardTitle>{feature.title}</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-base">
                  {feature.description}
                </CardDescription>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* Benefits Section */}
      <section className="bg-muted/50 rounded-lg p-8 md:p-12">
        <div className="max-w-3xl mx-auto space-y-8">
          <div className="text-center space-y-4">
            <h2 className="text-3xl md:text-4xl font-bold">
              Why Landlords Choose Homespaces
            </h2>
            <p className="text-lg text-muted-foreground">
              Join thousands of property owners who trust us with their
              investments
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            {benefits.map((benefit) => (
              <div key={benefit} className="flex items-start gap-3">
                <CheckCircle2 className="h-6 w-6 text-primary flex-shrink-0 mt-0.5" />
                <p className="text-base">{benefit}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Preview */}
      <section className="space-y-12">
        <div className="text-center space-y-4">
          <h2 className="text-3xl md:text-4xl font-bold">
            Simple, Transparent Pricing
          </h2>
          <p className="text-lg text-muted-foreground">
            Choose the plan that fits your portfolio size
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
          <Card>
            <CardHeader>
              <CardTitle>Starter</CardTitle>
              <CardDescription>
                Perfect for individual landlords
              </CardDescription>
              <div className="pt-4">
                <span className="text-4xl font-bold">Free</span>
                <span className="text-muted-foreground">/month</span>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <ul className="space-y-2 text-sm">
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-primary" />
                  Up to 3 properties
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-primary" />
                  Basic tenant screening
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-primary" />
                  Digital lease agreements
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-primary" />
                  Maintenance tracking
                </li>
              </ul>
              <Button className="w-full" variant="outline" asChild>
                <Link to="/auth/signup">Get Started</Link>
              </Button>
            </CardContent>
          </Card>

          <Card className="border-primary shadow-lg">
            <CardHeader>
              <Badge className="w-fit mb-2">MOST POPULAR</Badge>
              <CardTitle>Professional</CardTitle>
              <CardDescription>For growing property portfolios</CardDescription>
              <div className="pt-4">
                <span className="text-4xl font-bold">₦15,000</span>
                <span className="text-muted-foreground">/month</span>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <ul className="space-y-2 text-sm">
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-primary" />
                  Up to 15 properties
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-primary" />
                  Advanced analytics
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-primary" />
                  Automated rent collection
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-primary" />
                  Priority support
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-primary" />
                  Custom branding
                </li>
              </ul>
              <Button className="w-full" asChild>
                <Link to="/auth/signup">Start Free Trial</Link>
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Enterprise</CardTitle>
              <CardDescription>For large property portfolios</CardDescription>
              <div className="pt-4">
                <span className="text-4xl font-bold">Custom</span>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <ul className="space-y-2 text-sm">
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-primary" />
                  Unlimited properties
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-primary" />
                  Dedicated account manager
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-primary" />
                  API access
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-primary" />
                  Custom integrations
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-primary" />
                  24/7 phone support
                </li>
              </ul>
              <Button className="w-full" variant="outline" asChild>
                <Link to="/contact">Contact Sales</Link>
              </Button>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-primary text-primary-foreground rounded-lg p-8 md:p-12 text-center space-y-6">
        <h2 className="text-3xl md:text-4xl font-bold">
          Ready to Transform Your Property Management?
        </h2>
        <p className="text-lg max-w-2xl mx-auto opacity-90">
          Join thousands of landlords who have streamlined their operations and
          increased their ROI with Homespaces.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
          <Button size="lg" variant="secondary" asChild>
            <Link to="/landlord/dashboard">
              Access Dashboard
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </Button>
          <Button
            size="lg"
            variant="outline"
            className="bg-transparent text-primary-foreground border-primary-foreground hover:bg-primary-foreground hover:text-primary"
            asChild
          >
            <Link to="/contact">Schedule a Demo</Link>
          </Button>
        </div>
      </section>
    </div>
  );
}
